import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';
import { AuthSessionService } from '../../auth/auth-session.service';
import { SidebarService } from '../sidebar/sidebar.service';
import { breadcrumbTrail, buildNavItems } from '../sidebar/nav-items';
import { BreadcrumbExtraService } from '../breadcrumb-extra.service';

interface Breadcrumb {
  label: string;
  isHome?: boolean;
  /** null = sin navegación propia (ej. un agrupador sin vista real). */
  route?: string | null;
}

interface UserMenuOption {
  label: string;
  action: string;
}

@Component({
  selector: 'muni-admin-header',
  imports: [RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  protected readonly sidebarSvc = inject(SidebarService);
  private readonly session = inject(AuthSessionService);
  private readonly router = inject(Router);
  private readonly breadcrumbExtra = inject(BreadcrumbExtraService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  protected readonly breadcrumbs = computed<Breadcrumb[]>(() => {
    const url = this.currentUrl().split('?')[0].split('#')[0];
    const items = buildNavItems(this.session.menu(), this.session.user());
    const trail: Breadcrumb[] = breadcrumbTrail(items, url).map((item) => ({
      label: item.label,
      route: item.route,
    }));

    const override = this.breadcrumbExtra.override();
    const extra: Breadcrumb[] =
      override && override.url === url
        ? override.labels.map((label) => ({ label, route: null }))
        : [];

    return [{ label: 'Inicio', isHome: true, route: '/' }, ...trail, ...extra];
  });

  protected readonly user = signal({ nombre: 'Usuario', dni: '—' });

  protected readonly menuOpen = signal(false);

  protected readonly userMenu = signal<UserMenuOption[]>([
    { label: 'Mi perfil', action: 'profile' },
    { label: 'Configuración', action: 'settings' },
    { label: 'Cerrar sesión', action: 'logout' },
  ]);

  protected readonly iniciales = computed(() =>
    this.user()
      .nombre.split(' ')
      .map((parte) => parte.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase()
  );

  constructor() {
    const account = this.session.user();
    if (!account) return;
    // El login ya devuelve la identidad necesaria para pintar el encabezado.
    // No se vuelve a consultar /administrators/:id: además de ser redundante,
    // un 401 de ese endpoint opcional no debe invalidar una sesión recién creada.
    this.user.set({ nombre: account.email.split('@')[0], dni: '—' });
  }

  protected toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }
}
